class Step {
    constructor(
        public value: string,
        public optimized = false,
    ) {}

    toString(): string {
        return this.value;
    }
}

export class DOMPath {
    xPath(node: Node, optimized: boolean): string {
        if (node.nodeType === Node.DOCUMENT_NODE) {
            return '/';
        }

        const steps: Step[] = [];
        let contextNode: Node | null = node;

        while (contextNode) {
            const step = this.xPathValue(contextNode, optimized);

            if (!step) {
                break;
            } // Error - bail out early.

            steps.push(step);

            if (step.optimized) {
                break;
            }

            contextNode = contextNode.parentNode;
        }

        steps.reverse();

        return (steps.length && steps[0]?.optimized ? '' : '/') + steps.join('/');
    }

    private xPathValue(node: Node, optimized: boolean): Step | null {
        const ownIndex = this.xPathIndex(node);
        let ownValue: string;

        if (ownIndex === -1) {
            return null;
        } // Error.

        switch (node.nodeType) {
            case Node.ELEMENT_NODE: {
                const elem = node as Element;

                if (optimized && elem.getAttribute('id')) {
                    return new Step('//*[@id="' + elem.getAttribute('id') + '"]', true);
                }
                ownValue = elem.localName;
                break;
            }
            case Node.ATTRIBUTE_NODE:
                ownValue = '@' + node.nodeName;
                break;
            case Node.TEXT_NODE:
            case Node.CDATA_SECTION_NODE:
                ownValue = 'text()';
                break;
            case Node.PROCESSING_INSTRUCTION_NODE:
                ownValue = 'processing-instruction()';
                break;
            case Node.COMMENT_NODE:
                ownValue = 'comment()';
                break;
            case Node.DOCUMENT_NODE:
                ownValue = '';
                break;
            default:
                ownValue = '';
                break;
        }

        if (ownIndex > 0) {
            ownValue += '[' + ownIndex + ']';
        }

        return new Step(ownValue, node.nodeType === Node.DOCUMENT_NODE);
    }

    private xPathIndex(node: Node) {
        // Returns -1 in case of error, 0 if no siblings matching the same expression,
        // <XPath index among the same expression-matching sibling nodes> otherwise.

        const siblings: any = node.parentNode ? node.parentNode.children : null;

        if (!siblings) {
            return 0;
        } // Root node - no siblings.

        let hasSameNamedElements = false;

        for (const sibling of siblings) {
            if (this.areNodesSimilar(node, sibling) && sibling !== node) {
                hasSameNamedElements = true;
                break;
            }
        }

        if (!hasSameNamedElements) {
            return 0;
        }

        let ownIndex = 1; // XPath indices start with 1.

        for (const sibling of siblings) {
            if (this.areNodesSimilar(node, sibling)) {
                if (sibling === node) {
                    return ownIndex;
                }
                ++ownIndex;
            }
        }

        return -1; // An error occurred: |node| not found in parent's children.
    }

    private areNodesSimilar(left: Node, right: Node) {
        if (left === right) {
            return true;
        }

        if (left.nodeType === Node.ELEMENT_NODE && right.nodeType === Node.ELEMENT_NODE) {
            return (left as Element).localName === (right as Element).localName;
        }

        if (left.nodeType === right.nodeType) {
            return true;
        }

        // XPath treats CDATA as text nodes.
        const leftType = left.nodeType === Node.CDATA_SECTION_NODE ? Node.TEXT_NODE : left.nodeType;
        const rightType =
            right.nodeType === Node.CDATA_SECTION_NODE ? Node.TEXT_NODE : right.nodeType;

        return leftType === rightType;
    }
}
